import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * L4M cart drawer bridge.
 *
 * Several custom L4M sections use regular Shopify product forms instead of
 * Horizon's <product-form-component>. A regular product form performs a full
 * navigation to /cart after submit, so the cart drawer never gets a chance to
 * open. This bridge upgrades those unhandled forms to the same AJAX/event flow
 * Horizon uses, without touching forms already handled by product-form.js.
 */

const DESKTOP_DRAWER_QUERY = window.matchMedia('(min-width: 990px)');

function isCartAddForm(form) {
  if (!(form instanceof HTMLFormElement)) return false;

  const action = form.getAttribute('action') || '';
  const dataType = form.dataset.type || '';

  return action.includes('/cart/add') || dataType === 'add-to-cart-form';
}

function getSectionIds() {
  const ids = new Set(['cart-drawer-section']);

  document.querySelectorAll('cart-items-component[data-section-id]').forEach((component) => {
    const id = component.dataset.sectionId;
    if (id) ids.add(id);
  });

  return [...ids];
}

async function fetchCart() {
  const cartUrl = window.Theme?.routes?.cart_url || '/cart';
  const response = await fetch(`${cartUrl}.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });

  if (!response.ok) throw new Error(`Failed to refresh cart: ${response.status}`);
  return response.json();
}

async function openCartDrawer() {
  const drawer = document.getElementById('cart-drawer');
  if (!drawer) return false;

  if (window.customElements?.whenDefined) {
    try {
      await customElements.whenDefined('theme-drawer');
    } catch (_) {}
  }

  if (typeof drawer.open === 'function') {
    drawer.open();
    return true;
  }

  return false;
}

function setBusy(form, busy) {
  form.toggleAttribute('aria-busy', busy);
  form.querySelectorAll('button[type="submit"], input[type="submit"]').forEach((button) => {
    if (!(button instanceof HTMLButtonElement || button instanceof HTMLInputElement)) return;

    if (busy) {
      button.dataset.l4mWasDisabled = button.disabled ? 'true' : 'false';
      button.disabled = true;
    } else {
      const wasDisabled = button.dataset.l4mWasDisabled === 'true';
      delete button.dataset.l4mWasDisabled;
      if (!wasDisabled) button.disabled = false;
    }
  });
}

async function handleUnhandledProductForm(event) {
  if (event.defaultPrevented) return;
  if (!DESKTOP_DRAWER_QUERY.matches) return;

  const form = event.target;
  if (!isCartAddForm(form)) return;

  // On /cart there is intentionally no cart drawer, so keep Shopify's normal
  // cart-page behaviour there.
  const drawer = document.getElementById('cart-drawer');
  if (!drawer) return;

  event.preventDefault();

  // Prevent accidental double submits while the request is in flight.
  if (form.dataset.l4mCartSubmitting === 'true') return;
  form.dataset.l4mCartSubmitting = 'true';
  setBusy(form, true);

  const formData = new FormData(form);
  const variantId = formData.get('id');
  const quantity = Number(formData.get('quantity')) || 1;
  const sectionIds = getSectionIds();
  formData.set('sections', sectionIds.join(','));
  formData.set('sections_url', window.location.pathname + window.location.search);

  const deferred = CartLinesUpdateEvent.createPromise();
  form.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: variantId
        ? [
            {
              merchandiseId: String(variantId),
              quantity,
            },
          ]
        : [],
      promise: deferred.promise,
    })
  );

  // Match Horizon's normal experience: open immediately, then hydrate the
  // drawer with the returned section HTML when the request completes.
  openCartDrawer();

  try {
    const addUrl = window.Theme?.routes?.cart_add_url || '/cart/add.js';
    const response = await fetch(addUrl, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: formData,
      credentials: 'same-origin',
    });

    const payload = await response.json();

    if (!response.ok || payload?.status) {
      const message = payload?.description || payload?.message || 'Could not add this item to the cart.';
      throw new Error(message);
    }

    const ajaxCart = await fetchCart();

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
      detail: {
        items: ajaxCart.items,
        source: 'l4m-cart-drawer-bridge',
        sourceId: form.id || 'l4m-product-form',
        itemCount: quantity,
        productId: form.closest('[data-product-id]')?.dataset.productId,
        sections: payload.sections,
        didError: false,
      },
    });

    // Re-open after hydration as a hard guarantee in case another component
    // temporarily changed drawer state during the cart update.
    requestAnimationFrame(() => openCartDrawer());
  } catch (error) {
    deferred.reject(error);

    form.dispatchEvent(
      new CartErrorEvent({
        error: error?.message || 'Could not add this item to the cart.',
        code: 'SERVICE_UNAVAILABLE',
      })
    );

    // Keep the customer on the product page instead of silently navigating
    // away. Native Horizon error UI will handle forms that provide one.
    console.error('[l4m-cart-drawer-bridge]', error);
  } finally {
    delete form.dataset.l4mCartSubmitting;
    setBusy(form, false);
  }
}

// Bubble-phase listener is deliberate: Horizon's native product-form handler
// runs first and calls preventDefault(). We only take over forms nobody else
// handled, so there are no duplicate cart additions.
document.addEventListener('submit', handleUnhandledProductForm);
